import api from './api';
import productoService from './productoService';
export interface DescuentoDto {
  codigoLista?: string | null;
  nroCliente?: number;
  codigoRuta?: number;
  nroVendedor?: number;
  atributo1Cli?: string | null;
  atributo2Cli?: string | null;
  codigoArticulo?: string | null;
  codigoLinea?: string | null;
  codigoRamo?: string | null;
  codigoClase?: string | null;
  atributo1Sto?: string | null;
  atributo2Sto?: string | null;
  /** Cantidad mínima del renglón para que aplique (tramo por volumen). 0 = cualquier cantidad. */
  cantidadDesde?: number | null;
  porcDesc: number;
  fechaVigencia?: string | null;
  /** P = pendiente, A = activo, I = inactivo. */
  estado?: EstadoDescuento;
  fechaDesde?: string | null;
  fechaAprobacion?: string | null;
}

export type EstadoDescuento = 'P' | 'A' | 'I';

export const ESTADO_DESCUENTO_LABEL: Record<EstadoDescuento, string> = {
  P: 'Pendiente',
  A: 'Activo',
  I: 'Inactivo',
};

export function normalizarEstadoDescuento(value: unknown): EstadoDescuento {
  const e = String(value ?? '').trim().toUpperCase();
  return e === 'P' || e === 'I' ? e : 'A';
}

/** YYYY-MM-DD de hoy en hora local. */
export function hoyIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export type CreateDescuentoDto = DescuentoDto;
export type UpdateDescuentoDto = DescuentoDto;

export interface DescuentoKey {
  codigoLista?: string | null;
  nroCliente?: number | null;
  codigoRuta?: number | null;
  nroVendedor?: number | null;
  atributo1Cli?: string | null;
  atributo2Cli?: string | null;
  codigoArticulo?: string | null;
  codigoLinea?: string | null;
  codigoRamo?: string | null;
  codigoClase?: string | null;
  atributo1Sto?: string | null;
  atributo2Sto?: string | null;
  cantidadDesde?: number | null;
}

export interface DescuentoSearchParams extends DescuentoKey {
  searchTerm?: string;
  estado?: EstadoDescuento;
  pageNumber?: number;
  pageSize?: number;
}

export interface DescuentoPagedResult {
  items: DescuentoDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

/** Contexto de cabecera de venta para pedir el % al backend. */
export interface ContextoDescuentoVenta {
  codigoLista?: string | null;
  nroCliente?: number | null;
  codigoRuta?: number | null;
  nroVendedor?: number | null;
  atributo1Cli?: string | null;
  atributo2Cli?: string | null;
}

export interface ResolverDescuentoRequest extends DescuentoKey {
  cantidad?: number | null;
  um?: string | null;
  fecha?: string | null;
}

export interface ResolverDescuentoResult {
  porcDesc: number;
  fechaVigencia?: string | null;
}

const INT_KEY_FIELDS = ['nroCliente', 'codigoRuta', 'nroVendedor'] as const;
const STR_KEY_FIELDS = [
  'codigoLista',
  'atributo1Cli',
  'atributo2Cli',
  'codigoArticulo',
  'codigoLinea',
  'codigoRamo',
  'codigoClase',
  'atributo1Sto',
  'atributo2Sto',
] as const;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function readStr(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = obj[key];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

function readNum(obj: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const n = Number(obj[key]);
    if (Number.isFinite(n) && n !== 0) return n;
  }
  return 0;
}

function readNumAllowZero(obj: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    if (obj[key] == null || obj[key] === '') continue;
    const n = Number(obj[key]);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

const STOCK_DIMS = [
  'codigoArticulo',
  'codigoLinea',
  'codigoRamo',
  'codigoClase',
  'atributo1Sto',
  'atributo2Sto',
] as const;

/** Normaliza DTO camelCase / PascalCase y alias de porcentaje. */
export function normalizeDescuentoDto(raw: unknown): DescuentoDto {
  const o = asRecord(raw);
  return {
    codigoLista: readStr(o, 'codigoLista', 'CodigoLista') || null,
    nroCliente: readNumAllowZero(o, 'nroCliente', 'NroCliente'),
    codigoRuta: readNumAllowZero(o, 'codigoRuta', 'CodigoRuta'),
    nroVendedor: readNumAllowZero(o, 'nroVendedor', 'NroVendedor'),
    atributo1Cli: readStr(o, 'atributo1Cli', 'Atributo1Cli') || null,
    atributo2Cli: readStr(o, 'atributo2Cli', 'Atributo2Cli') || null,
    codigoArticulo: readStr(o, 'codigoArticulo', 'CodigoArticulo') || null,
    codigoLinea: readStr(o, 'codigoLinea', 'CodigoLinea', 'linea', 'Linea') || null,
    codigoRamo: readStr(o, 'codigoRamo', 'CodigoRamo', 'ramo', 'Ramo') || null,
    codigoClase: readStr(o, 'codigoClase', 'CodigoClase', 'clase', 'Clase') || null,
    atributo1Sto: readStr(o, 'atributo1Sto', 'Atributo1Sto') || null,
    atributo2Sto: readStr(o, 'atributo2Sto', 'Atributo2Sto') || null,
    cantidadDesde: readNumAllowZero(o, 'cantidadDesde', 'CantidadDesde'),
    porcDesc: readNumAllowZero(o, 'porcDesc', 'PorcDesc', 'porc_Desc', 'Porc_Desc'),
    fechaVigencia: (readStr(o, 'fechaVigencia', 'FechaVigencia') || null) as string | null,
    estado: normalizarEstadoDescuento(o.estado ?? o.Estado),
    fechaDesde: (readStr(o, 'fechaDesde', 'FechaDesde') || null) as string | null,
    fechaAprobacion: (readStr(o, 'fechaAprobacion', 'FechaAprobacion') || null) as string | null,
  };
}

function dimStr(value: unknown): string {
  return String(value ?? '').trim();
}

/** Códigos 0 / "0" / vacío = sin filtro (comodín de la regla). */
function dimKeyActiva(value: unknown): boolean {
  const s = dimStr(value);
  return Boolean(s) && s !== '0';
}

function dimNum(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function reglaTieneStock(regla: DescuentoDto): boolean {
  return STOCK_DIMS.some((k) => dimKeyActiva(regla[k]));
}

export function esReglaCabecera(regla: DescuentoDto): boolean {
  return !reglaTieneStock(regla);
}

/** YYYY-MM-DD (o ISO) como día calendario local, sin corrimiento UTC. */
function calendarioDesdeIso(raw: string): Date | null {
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Vacío = sin caducidad. Con fecha: vigente todo ese día; al día siguiente ya no aplica. */
export function vigenciaReglaOk(regla: DescuentoDto, fecha: Date = new Date()): boolean {
  const raw = dimStr(regla.fechaVigencia);
  if (!raw) return true;
  const caduca = calendarioDesdeIso(raw);
  if (!caduca) return true;
  const hoy = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
  return hoy.getTime() <= caduca.getTime();
}

/** Solo reglas publicadas (activas) cuyo período ya empezó. */
export function reglaPublicadaOk(regla: DescuentoDto, fecha: Date = new Date()): boolean {
  if (normalizarEstadoDescuento(regla.estado) !== 'A') return false;
  const raw = dimStr(regla.fechaDesde);
  if (!raw) return true;
  const desde = calendarioDesdeIso(raw);
  if (!desde) return true;
  const hoy = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
  return desde.getTime() <= hoy.getTime();
}

function readStrFrom(
  obj: Record<string, unknown>,
  keys: string[],
  nested: string[] = [],
): string {
  const direct = readStr(obj, ...keys);
  if (direct) return direct;
  for (const n of nested) {
    const nestedObj = asRecord(obj[n]);
    const v = readStr(nestedObj, ...keys);
    if (v) return v;
  }
  return '';
}

/** Vacío / 0 en la regla = comodín. Si está cargado, debe coincidir con el contexto. */
export function reglaAplica(regla: DescuentoDto, ctx: ResolverDescuentoRequest): boolean {
  const strEq = (ruleVal: unknown, ctxVal: unknown) => {
    if (!dimKeyActiva(ruleVal)) return true;
    const r = dimStr(ruleVal);
    const c = dimStr(ctxVal);
    if (r.toUpperCase() === c.toUpperCase()) return true;
    const nr = Number(r);
    const nc = Number(c);
    return Number.isFinite(nr) && Number.isFinite(nc) && nr === nc;
  };
  const numEq = (ruleVal: unknown, ctxVal: unknown) => {
    const r = dimNum(ruleVal);
    if (!r) return true;
    return r === dimNum(ctxVal);
  };
  return (
    strEq(regla.codigoLista, ctx.codigoLista) &&
    numEq(regla.nroCliente, ctx.nroCliente) &&
    numEq(regla.codigoRuta, ctx.codigoRuta) &&
    numEq(regla.nroVendedor, ctx.nroVendedor) &&
    strEq(regla.atributo1Cli, ctx.atributo1Cli) &&
    strEq(regla.atributo2Cli, ctx.atributo2Cli) &&
    strEq(regla.codigoArticulo, ctx.codigoArticulo) &&
    strEq(regla.codigoLinea, ctx.codigoLinea) &&
    strEq(regla.codigoRamo, ctx.codigoRamo) &&
    strEq(regla.codigoClase, ctx.codigoClase) &&
    strEq(regla.atributo1Sto, ctx.atributo1Sto) &&
    strEq(regla.atributo2Sto, ctx.atributo2Sto) &&
    alcanzaCantidad(regla, ctx.cantidad)
  );
}

function cantidadDesdeRegla(regla: DescuentoDto): number {
  return Math.max(0, dimNum(regla.cantidadDesde));
}

/** Sin tramo (0) aplica a cualquier cantidad; con tramo, la cantidad del renglón debe alcanzarlo. */
export function alcanzaCantidad(regla: DescuentoDto, cantidad: unknown): boolean {
  const desde = cantidadDesdeRegla(regla);
  return !desde || dimNum(cantidad) >= desde;
}

export type TipoDatoCliente = 'cliente' | 'ruta' | 'vendedor' | 'atributo';

function cantidadAtributosCliente(regla: DescuentoDto): number {
  return [regla.atributo1Cli, regla.atributo2Cli].filter(dimKeyActiva).length;
}

/** Cliente > ruta > vendedor > atributos del cliente (más atributos, más específica); 0 si no tiene dato de cliente. */
export function prioridadDatoCliente(regla: DescuentoDto): number {
  if (dimNum(regla.nroCliente)) return 30;
  if (dimNum(regla.codigoRuta)) return 20;
  if (dimNum(regla.nroVendedor)) return 10;
  return cantidadAtributosCliente(regla);
}

/** Artículo > clase > línea > ramo > atributos del artículo. */
export function prioridadDatoArticulo(regla: DescuentoDto): number {
  return (
    (dimKeyActiva(regla.codigoArticulo) ? 10000 : 0) +
    (dimKeyActiva(regla.codigoClase) ? 1000 : 0) +
    (dimKeyActiva(regla.codigoLinea) ? 100 : 0) +
    (dimKeyActiva(regla.codigoRamo) ? 10 : 0) +
    (dimKeyActiva(regla.atributo1Sto) ? 1 : 0) +
    (dimKeyActiva(regla.atributo2Sto) ? 1 : 0)
  );
}

/** Lista > dato del cliente > dato del artículo > tramo de cantidad más alto alcanzado > mayor %. */
export function compararPrioridadRegla(a: DescuentoDto, b: DescuentoDto): number {
  return (
    Number(dimKeyActiva(b.codigoLista)) - Number(dimKeyActiva(a.codigoLista)) ||
    prioridadDatoCliente(b) - prioridadDatoCliente(a) ||
    prioridadDatoArticulo(b) - prioridadDatoArticulo(a) ||
    cantidadDesdeRegla(b) - cantidadDesdeRegla(a) ||
    clampPorc(b.porcDesc) - clampPorc(a.porcDesc)
  );
}

/** Misma validación que el backend al crear una regla; null si es válida. */
export function validarDimensionesRegla(regla: DescuentoDto): string | null {
  const datosCliente =
    [regla.nroCliente, regla.codigoRuta, regla.nroVendedor].filter((v) => dimNum(v) > 0).length +
    (cantidadAtributosCliente(regla) > 0 ? 1 : 0);
  if (datosCliente === 0) return 'Indicá un dato del cliente: cliente, ruta, vendedor o atributos.';
  if (datosCliente > 1) {
    return 'Solo se puede indicar uno de los datos del cliente: cliente, ruta, vendedor o atributos.';
  }
  if (!reglaTieneStock(regla)) {
    return 'Indicá al menos un dato del artículo: artículo, ramo, línea, clase o atributo.';
  }
  if (atributoRepetido(regla.atributo1Cli, regla.atributo2Cli) || atributoRepetido(regla.atributo1Sto, regla.atributo2Sto)) {
    return 'El mismo atributo no puede estar en la posición 1 y 2.';
  }
  if (dimNum(regla.cantidadDesde) < 0) return 'La cantidad desde no puede ser negativa.';
  return null;
}

function atributoRepetido(pos1: unknown, pos2: unknown): boolean {
  return dimKeyActiva(pos1) && dimStr(pos1).toUpperCase() === dimStr(pos2).toUpperCase();
}

/**
 * En modo línea una regla sin dato del artículo también aplica al renglón, pero pierde contra
 * cualquier regla con dato del artículo (mismo criterio que el backend).
 */
export function elegirMejorRegla(
  reglas: DescuentoDto[],
  ctx: ResolverDescuentoRequest,
  modo: 'cabecera' | 'linea',
  fecha: Date = new Date(),
): DescuentoDto | null {
  const candidatas = reglas.filter((r) => {
    if (!reglaPublicadaOk(r, fecha) || !vigenciaReglaOk(r, fecha)) return false;
    if (modo === 'cabecera' && !esReglaCabecera(r)) return false;
    return reglaAplica(r, ctx);
  });
  if (!candidatas.length) return null;
  candidatas.sort(
    (a, b) => Number(reglaTieneStock(b)) - Number(reglaTieneStock(a)) || compararPrioridadRegla(a, b),
  );
  return candidatas[0];
}

function clampPorc(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

/** % Desc 1/2/3 del maestro de cliente (no de la tabla Descuentos). */
export function leerPorcDescMaestroCliente(src: unknown): {
  porcDesc: number;
  porcDesc2: number;
  porcDesc3: number;
} {
  const o = asRecord(src);
  return {
    porcDesc: clampPorc(o.porcDesc ?? o.porc_Desc ?? o.porcdesc ?? o.PorcDesc),
    porcDesc2: clampPorc(o.porcDesc2 ?? o.porc_Desc2 ?? o.porcdesc2 ?? o.PorcDesc2),
    porcDesc3: clampPorc(o.porcDesc3 ?? o.porc_Desc3 ?? o.porcdesc3 ?? o.PorcDesc3),
  };
}

let descuentosCache: { at: number; items: DescuentoDto[] } | null = null;
const DESCUENTOS_CACHE_MS = 15_000;

export function invalidateDescuentosCache(): void {
  descuentosCache = null;
}

async function listAllDescuentos(): Promise<DescuentoDto[]> {
  const now = Date.now();
  if (descuentosCache && now - descuentosCache.at < DESCUENTOS_CACHE_MS) {
    return descuentosCache.items;
  }
  const items: DescuentoDto[] = [];
  let page = 1;
  const pageSize = 100;
  for (let i = 0; i < 10; i += 1) {
    const pageResult = await descuentoService.getDescuentos({ pageNumber: page, pageSize });
    items.push(...(pageResult.items ?? []));
    const total = Number(pageResult.totalCount) || items.length;
    if (items.length >= total || (pageResult.items ?? []).length < pageSize) break;
    page += 1;
  }
  descuentosCache = { at: now, items };
  return items;
}

function matchDesdeLista(
  reglas: DescuentoDto[],
  ctx: ResolverDescuentoRequest,
  modo: 'cabecera' | 'linea',
): ResolverDescuentoResult {
  const best = elegirMejorRegla(reglas, ctx, modo);
  if (!best) return { porcDesc: 0, fechaVigencia: null };
  return { porcDesc: clampPorc(best.porcDesc), fechaVigencia: best.fechaVigencia ?? null };
}

/** Clave para by-key: números no usados = 0, strings no usadas = ''. */
export function toDescuentoKeyParams(key: DescuentoKey): Record<string, string | number> {
  return {
    codigoLista: (key.codigoLista ?? '').trim(),
    nroCliente: Number(key.nroCliente) || 0,
    codigoRuta: Number(key.codigoRuta) || 0,
    nroVendedor: Number(key.nroVendedor) || 0,
    atributo1Cli: (key.atributo1Cli ?? '').trim(),
    atributo2Cli: (key.atributo2Cli ?? '').trim(),
    codigoArticulo: (key.codigoArticulo ?? '').trim(),
    codigoLinea: (key.codigoLinea ?? '').trim(),
    codigoRamo: (key.codigoRamo ?? '').trim(),
    codigoClase: (key.codigoClase ?? '').trim(),
    atributo1Sto: (key.atributo1Sto ?? '').trim(),
    atributo2Sto: (key.atributo2Sto ?? '').trim(),
    cantidadDesde: Math.max(0, Number(key.cantidadDesde) || 0),
  };
}

export function toCreatePayload(dto: DescuentoDto): CreateDescuentoDto {
  const key = toDescuentoKeyParams(dto);
  const fecha = dto.fechaVigencia ? String(dto.fechaVigencia).trim() : '';
  return {
    codigoLista: String(key.codigoLista) || null,
    nroCliente: Number(key.nroCliente) || 0,
    codigoRuta: Number(key.codigoRuta) || 0,
    nroVendedor: Number(key.nroVendedor) || 0,
    atributo1Cli: String(key.atributo1Cli) || null,
    atributo2Cli: String(key.atributo2Cli) || null,
    codigoArticulo: String(key.codigoArticulo) || null,
    codigoLinea: String(key.codigoLinea) || null,
    codigoRamo: String(key.codigoRamo) || null,
    codigoClase: String(key.codigoClase) || null,
    atributo1Sto: String(key.atributo1Sto) || null,
    atributo2Sto: String(key.atributo2Sto) || null,
    cantidadDesde: Number(key.cantidadDesde) || 0,
    porcDesc: Number(dto.porcDesc) || 0,
    fechaVigencia: fecha || null,
    fechaDesde: dto.fechaDesde ? String(dto.fechaDesde).trim().slice(0, 10) : null,
  };
}

export function toEditSearch(key: DescuentoDto | DescuentoKey): string {
  const params = new URLSearchParams();
  Object.entries(toDescuentoKeyParams(key)).forEach(([k, v]) => params.set(k, String(v)));
  const dto = key as DescuentoDto;
  if (dto.porcDesc != null) params.set('porcDesc', String(Number(dto.porcDesc) || 0));
  const fecha = dto.fechaVigencia ? String(dto.fechaVigencia).trim() : '';
  if (fecha) params.set('fechaVigencia', fecha);
  return params.toString();
}

export function fromSearchParams(sp: URLSearchParams): DescuentoKey {
  return {
    codigoLista: sp.get('codigoLista') || '',
    nroCliente: Number(sp.get('nroCliente')) || 0,
    codigoRuta: Number(sp.get('codigoRuta')) || 0,
    nroVendedor: Number(sp.get('nroVendedor')) || 0,
    atributo1Cli: sp.get('atributo1Cli') || '',
    atributo2Cli: sp.get('atributo2Cli') || '',
    codigoArticulo: sp.get('codigoArticulo') || '',
    codigoLinea: sp.get('codigoLinea') || '',
    codigoRamo: sp.get('codigoRamo') || '',
    codigoClase: sp.get('codigoClase') || '',
    atributo1Sto: sp.get('atributo1Sto') || '',
    atributo2Sto: sp.get('atributo2Sto') || '',
    cantidadDesde: Number(sp.get('cantidadDesde')) || 0,
  };
}

export function fromEditSearch(sp: URLSearchParams): DescuentoDto {
  const key = fromSearchParams(sp);
  const fecha = (sp.get('fechaVigencia') || '').trim();
  return {
    codigoLista: key.codigoLista || '',
    nroCliente: Number(key.nroCliente) || 0,
    codigoRuta: Number(key.codigoRuta) || 0,
    nroVendedor: Number(key.nroVendedor) || 0,
    atributo1Cli: key.atributo1Cli || '',
    atributo2Cli: key.atributo2Cli || '',
    codigoArticulo: key.codigoArticulo || '',
    codigoLinea: key.codigoLinea || '',
    codigoRamo: key.codigoRamo || '',
    codigoClase: key.codigoClase || '',
    atributo1Sto: key.atributo1Sto || '',
    atributo2Sto: key.atributo2Sto || '',
    cantidadDesde: Number(key.cantidadDesde) || 0,
    porcDesc: Number(sp.get('porcDesc')) || 0,
    fechaVigencia: fecha || null,
  };
}

function unwrapDescuentoPayload(raw: unknown): unknown {
  if (Array.isArray(raw)) return raw[0];
  const o = asRecord(raw);
  return o.item ?? o.Item ?? o.data ?? o.Data ?? o.result ?? o.Result ?? raw;
}

export function descuentoKeyId(key: DescuentoKey): string {
  return Object.entries(toDescuentoKeyParams(key))
    .map(([k, v]) => `${k}=${v}`)
    .join('|');
}

function intDisplay(value: number | null | undefined): string {
  const n = Number(value);
  return Number.isFinite(n) && n !== 0 ? String(n) : '';
}

function strDisplay(value: string | null | undefined): string {
  return (value ?? '').trim();
}

/** Texto de dimensiones cargadas (omite 0 / vacío). */
export function describirDimensiones(d: DescuentoDto): string {
  const parts: string[] = [];
  const lista = strDisplay(d.codigoLista);
  if (lista) parts.push(`Lista ${lista}`);
  const cli = intDisplay(d.nroCliente);
  if (cli) parts.push(`Cliente ${cli}`);
  const ruta = intDisplay(d.codigoRuta);
  if (ruta) parts.push(`Ruta ${ruta}`);
  const vend = intDisplay(d.nroVendedor);
  if (vend) parts.push(`Vendedor ${vend}`);
  const art = strDisplay(d.codigoArticulo);
  if (art) parts.push(`Art. ${art}`);
  const ramo = strDisplay(d.codigoRamo);
  if (ramo) parts.push(`Ramo ${ramo}`);
  const linea = strDisplay(d.codigoLinea);
  if (linea) parts.push(`Línea ${linea}`);
  const clase = strDisplay(d.codigoClase);
  if (clase) parts.push(`Clase ${clase}`);
  const a1c = strDisplay(d.atributo1Cli);
  if (a1c) parts.push(`Atr. cli 1 ${a1c}`);
  const a2c = strDisplay(d.atributo2Cli);
  if (a2c) parts.push(`Atr. cli 2 ${a2c}`);
  const a1s = strDisplay(d.atributo1Sto);
  if (a1s) parts.push(`Atr. art 1 ${a1s}`);
  const a2s = strDisplay(d.atributo2Sto);
  if (a2s) parts.push(`Atr. art 2 ${a2s}`);
  return parts.length ? parts.join(' · ') : 'General';
}

export function contextoDesdeProducto(producto: unknown): Pick<
  DescuentoKey,
  'codigoArticulo' | 'codigoLinea' | 'codigoRamo' | 'codigoClase' | 'atributo1Sto' | 'atributo2Sto'
> {
  const o = asRecord(producto);
  return {
    codigoArticulo: readStr(o, 'codigoArticulo', 'CodigoArticulo'),
    codigoLinea: readStrFrom(
      o,
      ['codigoLinea', 'CodigoLinea'],
      ['codigoLineaNavigation', 'CodigoLineaNavigation', 'linea', 'Linea'],
    ),
    codigoRamo: readStrFrom(
      o,
      ['codigoRamo', 'CodigoRamo'],
      ['codigoRamoNavigation', 'CodigoRamoNavigation', 'ramo', 'Ramo'],
    ),
    codigoClase: readStrFrom(
      o,
      ['codigoClase', 'CodigoClase'],
      ['codigoClaseNavigation', 'CodigoClaseNavigation', 'clase', 'Clase'],
    ),
    atributo1Sto: readStr(o, 'atributo1Sto', 'Atributo1Sto', 'atributo1', 'Atributo1'),
    atributo2Sto: readStr(o, 'atributo2Sto', 'Atributo2Sto', 'atributo2', 'Atributo2'),
  };
}

export function contextoDescuentoDesdeCliente(
  cliente: unknown,
  extras?: { codigoLista?: string | null; nroVendedor?: number | null; codigoRuta?: number | null },
): ContextoDescuentoVenta {
  const o = asRecord(cliente);
  const listaNested = asRecord(o.listaPrecio ?? o.ListaPrecio);
  return {
    codigoLista:
      (extras?.codigoLista ?? '').trim() ||
      readStr(o, 'nroLista', 'NroLista', 'codigoLista', 'CodigoLista') ||
      readStr(listaNested, 'codigoLista', 'CodigoLista') ||
      null,
    nroCliente: readNum(o, 'nroCliente', 'NroCliente') || 0,
    codigoRuta: extras?.codigoRuta ?? (readNum(o, 'codigoRuta', 'CodigoRuta') || null),
    nroVendedor:
      extras?.nroVendedor ??
      (readNum(o, 'nroVendedor', 'NroVendedor', 'vendedor', 'Vendedor') || null),
    atributo1Cli: readStr(o, 'atributo1', 'Atributo1', 'atributo1Cli') || null,
    atributo2Cli: readStr(o, 'atributo2', 'Atributo2', 'atributo2Cli') || null,
  };
}

function listQueryParams(params: DescuentoSearchParams): Record<string, string | number> {
  const q: Record<string, string | number> = {
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? 10,
  };
  if (params.searchTerm?.trim()) q.searchTerm = params.searchTerm.trim();
  if (params.estado) q.estado = params.estado;
  for (const field of STR_KEY_FIELDS) {
    const v = (params[field] ?? '').toString().trim();
    if (v) q[field] = v;
  }
  for (const field of INT_KEY_FIELDS) {
    const n = Number(params[field]);
    if (Number.isFinite(n) && n !== 0) q[field] = n;
  }
  return q;
}

function normalizePaged(data: unknown, fallbackPage: number, fallbackSize: number): DescuentoPagedResult {
  if (Array.isArray(data)) {
    const items = data.map(normalizeDescuentoDto);
    return {
      items,
      totalCount: items.length,
      pageNumber: 1,
      pageSize: items.length,
      totalPages: 1,
    };
  }
  const obj = asRecord(data);
  const rawItems = Array.isArray(obj.items)
    ? obj.items
    : Array.isArray(obj.Items)
      ? obj.Items
      : [];
  const items = (rawItems as unknown[]).map(normalizeDescuentoDto);
  return {
    items,
    totalCount: Number(obj.totalCount ?? items.length) || 0,
    pageNumber: Number(obj.pageNumber ?? fallbackPage) || fallbackPage,
    pageSize: Number(obj.pageSize ?? fallbackSize) || fallbackSize,
    totalPages: Number(obj.totalPages ?? 0) || 0,
  };
}

const descuentoService = {
  getDescuentos: async (params: DescuentoSearchParams = {}): Promise<DescuentoPagedResult> => {
    const response = await api.get('/api/Descuentos', { params: listQueryParams(params) });
    return normalizePaged(response.data, params.pageNumber ?? 1, params.pageSize ?? 10);
  },

  getByKey: async (key: DescuentoKey): Promise<DescuentoDto> => {
    const response = await api.get('/api/Descuentos/by-key', {
      params: toDescuentoKeyParams(key),
    });
    return normalizeDescuentoDto(unwrapDescuentoPayload(response.data));
  },

  create: async (dto: CreateDescuentoDto): Promise<DescuentoDto> => {
    const response = await api.post('/api/Descuentos', toCreatePayload(dto));
    invalidateDescuentosCache();
    return normalizeDescuentoDto(response.data);
  },

  /** claveOriginal: clave con la que se leyó la regla, para poder cambiar sus datos mientras no esté activa. */
  update: async (dto: UpdateDescuentoDto, claveOriginal?: DescuentoKey): Promise<void> => {
    await api.put('/api/Descuentos', {
      ...toCreatePayload(dto),
      claveOriginal: claveOriginal ? toDescuentoKeyParams(claveOriginal) : null,
    });
    invalidateDescuentosCache();
  },

  publicar: async (key: DescuentoKey): Promise<DescuentoDto> => {
    const response = await api.post('/api/Descuentos/publicar', toDescuentoKeyParams(key));
    invalidateDescuentosCache();
    return normalizeDescuentoDto(response.data);
  },

  inactivar: async (key: DescuentoKey): Promise<DescuentoDto> => {
    const response = await api.post('/api/Descuentos/inactivar', toDescuentoKeyParams(key));
    invalidateDescuentosCache();
    return normalizeDescuentoDto(response.data);
  },

  remove: async (key: DescuentoKey): Promise<void> => {
    await api.delete('/api/Descuentos/by-key', { params: toDescuentoKeyParams(key) });
    invalidateDescuentosCache();
  },

  /** El backend es la fuente de verdad. La lista es la del documento (sin lista, el backend usa la del cliente). */
  resolver: async (ctx: ResolverDescuentoRequest): Promise<ResolverDescuentoResult> => {
    try {
      const response = await api.post<ResolverDescuentoResult>('/api/Descuentos/resolver', {
        codigoLista: dimStr(ctx.codigoLista) || null,
        nroCliente: dimNum(ctx.nroCliente) || null,
        nroVendedor: dimNum(ctx.nroVendedor) || null,
        codigoRuta: dimNum(ctx.codigoRuta) || null,
        atributo1Cli: dimStr(ctx.atributo1Cli) || null,
        atributo2Cli: dimStr(ctx.atributo2Cli) || null,
        codigoArticulo: dimStr(ctx.codigoArticulo),
        atributo1Sto: dimStr(ctx.atributo1Sto) || null,
        atributo2Sto: dimStr(ctx.atributo2Sto) || null,
        cantidad: dimNum(ctx.cantidad) || null,
        fecha: ctx.fecha ?? null,
      });
      const data = asRecord(response.data);
      const porc = Number(data.porcDesc ?? data.PorcDesc ?? 0);
      return {
        porcDesc: Number.isFinite(porc) ? clampPorc(porc) : 0,
        fechaVigencia: (data.fechaVigencia ?? data.FechaVigencia ?? null) as string | null,
      };
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404 && status !== 405) throw err;
      return matchDesdeLista(await listAllDescuentos(), ctx, 'linea');
    }
  },
};

export async function resolverPorcDescLinea(
  cabecera: ContextoDescuentoVenta | undefined,
  producto: unknown,
  cantidad: number,
  um: string,
): Promise<number> {
  let prod: unknown = producto;
  let dims = contextoDesdeProducto(prod);
  const codigo =
    dims.codigoArticulo || readStr(asRecord(prod), 'codigoArticulo', 'CodigoArticulo');
  if (!codigo) return 0;
  const faltaClasif =
    !dims.codigoRamo &&
    !dims.codigoLinea &&
    !dims.codigoClase &&
    !dims.atributo1Sto &&
    !dims.atributo2Sto;
  if (faltaClasif) {
    try {
      const full = await productoService.getProducto(codigo);
      prod = { ...asRecord(prod), ...asRecord(full) };
      dims = contextoDesdeProducto(prod);
    } catch {
      /* usar el producto original */
    }
  }
  try {
    const r = await descuentoService.resolver({
      ...(cabecera ?? {}),
      ...dims,
      codigoArticulo: codigo,
      cantidad: Number(cantidad) || 0,
      um: (um ?? '').trim() || null,
    });
    return Number.isFinite(r.porcDesc) ? r.porcDesc : 0;
  } catch {
    return 0;
  }
}

/**
 * Para pantallas sin marca de descuento manual: al cambiar la cantidad toma el % del nuevo tramo
 * solo si el renglón tenía el % de la oferta de su cantidad anterior; si no, lo cargaron a mano y se respeta.
 */
export async function reajustarPorcDescPorCantidad(
  cabecera: ContextoDescuentoVenta | undefined,
  producto: unknown,
  cantidadAnterior: number,
  cantidadNueva: number,
  um: string,
  porcActual: number,
): Promise<number> {
  if (cantidadAnterior === cantidadNueva) return porcActual;
  const [anterior, nuevo] = await Promise.all([
    resolverPorcDescLinea(cabecera, producto, cantidadAnterior, um),
    resolverPorcDescLinea(cabecera, producto, cantidadNueva, um),
  ]);
  return Math.abs((Number(porcActual) || 0) - anterior) < 0.0001 ? nuevo : porcActual;
}

/**
 * Desc 1/2/3 del documento: solo el maestro del cliente. Las reglas de la tabla Descuentos
 * (incluidas las sin artículo) van al % de cada renglón vía resolverPorcDescLinea.
 */
export async function resolverBonificacionesCliente(
  cliente: unknown,
  _extras?: { codigoLista?: string | null; nroVendedor?: number | null; codigoRuta?: number | null },
): Promise<{ porcDesc: number; porcDesc2: number; porcDesc3: number }> {
  return leerPorcDescMaestroCliente(cliente);
}

export interface LineaDescuentoRecalculable {
  id: string;
  codigoArticulo?: string | null;
  cantidad?: number | null;
  um?: string | null;
  descuentoManual?: boolean;
}

/**
 * Recalcula bonificaciones de cabecera y % de líneas no manuales cuando cambia el contexto
 * del documento (lista, cliente, vendedor). Las líneas sin regla vuelven a 0 para no arrastrar
 * descuentos que ya no corresponden.
 */
export async function recalcularDescuentosVenta(
  cliente: unknown,
  extras: { codigoLista?: string | null; nroVendedor?: number | null },
  lineas: LineaDescuentoRecalculable[],
): Promise<{
  cabecera: { porcDesc: number; porcDesc2: number; porcDesc3: number };
  porLinea: Map<string, number>;
}> {
  const ctx = contextoDescuentoDesdeCliente(cliente, extras);
  const [cabecera, resultados] = await Promise.all([
    resolverBonificacionesCliente(cliente, extras),
    Promise.all(
      lineas
        .filter((l) => !l.descuentoManual && String(l.codigoArticulo ?? '').trim())
        .map(async (l) => {
          const porc = await resolverPorcDescLinea(
            ctx,
            { codigoArticulo: String(l.codigoArticulo).trim() },
            Number(l.cantidad) || 0,
            String(l.um ?? ''),
          );
          return [l.id, porc] as const;
        }),
    ),
  ]);
  return { cabecera, porLinea: new Map(resultados) };
}

export default descuentoService;